import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import api from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import { ArrowUpRight, ArrowDown, ShieldCheck, BadgeCheck, Hammer, Ruler, Star, MapPin } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];
const LOGO = "https://customer-assets-eiarnc6j.emergentagent.net/job_a2603301-837e-467e-952a-d7e2edb4c197/artifacts/3ut7qksg_file_0000000043b081f5a40b26af34420fa3.webp";

const STATS = [
  { n: "15+", l: "Years of Experience" },
  { n: "300+", l: "Projects Completed" },
  { n: "1-Yr", l: "Workmanship Warranty" },
  { n: "100%", l: "Licensed & Insured" },
];

const PILLARS = [
  { icon: ShieldCheck, t: "Licensed & Insured", d: "Fully licensed and insured for your complete peace of mind on every project." },
  { icon: BadgeCheck, t: "1-Year Workmanship Warranty", d: "Every remodel is backed by a written one-year workmanship warranty." },
  { icon: Hammer, t: "Premium Craftsmanship", d: "Master craftsmen and select materials for finishes that last a lifetime." },
  { icon: Ruler, t: "Tailored Solutions", d: "Custom designs built around your lifestyle, taste and budget." },
];

const Hero = ({ heroImage }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const logoY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} className="relative h-screen w-full overflow-hidden bg-[#050505]">
      <div className="absolute inset-0" style={{ opacity: 0.18 }}>
        <img src={heroImage || "https://images.pexels.com/photos/2724748/pexels-photo-2724748.jpeg"} alt="" className="h-full w-full object-cover" />
      </div>
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 40%, rgba(212,175,55,0.14), transparent 55%)" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black" />

      <motion.div style={{ opacity }} className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}>
          <Overline className="justify-center">Design • Build • Transform</Overline>
        </motion.div>

        <motion.div
          style={{ y: logoY }}
          initial={{ opacity: 0, scale: 0.9, filter: "blur(8px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ delay: 0.5, duration: 1.4, ease: EASE }}
          className="relative mt-8"
        >
          <div className="absolute inset-0 -z-10 blur-3xl opacity-40" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.5), transparent 70%)" }} />
          <img data-testid="hero-logo" src={LOGO} alt="GI Remodeling" className="mx-auto w-[min(520px,80vw)] max-h-[56vh] object-contain drop-shadow-[0_20px_60px_rgba(0,0,0,0.8)]" />
        </motion.div>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }} className="mt-8 max-w-xl text-white/70 font-body text-base md:text-lg leading-relaxed">
          Luxury home remodeling built on trust and craftsmanship. Proudly serving homeowners across Virginia — backed by a 1-year workmanship warranty.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.8 }} className="mt-9 flex flex-col sm:flex-row items-center gap-4">
          <Link data-testid="hero-cta" to="/contact" className="group inline-flex items-center gap-3 bg-gold text-black text-sm font-semibold uppercase tracking-widest px-8 py-4 hover:bg-white transition-colors duration-300">
            Request a Free Quote
            <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
          </Link>
          <Link data-testid="hero-about" to="/about" className="inline-flex items-center gap-2 text-white/80 hover:text-gold text-sm uppercase tracking-widest transition-colors">
            Who We Are
          </Link>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="mt-8 inline-flex items-center gap-2 text-white/50 text-xs uppercase tracking-[0.2em] font-body">
          <MapPin size={14} className="text-gold" /> Serving all of Virginia
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 text-white/40 text-xs uppercase tracking-[0.2em] font-body">
        Scroll <ArrowDown size={16} className="animate-bounce text-gold" />
      </motion.div>
    </section>
  );
};

const WhoWeAre = () => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36">
    <div className="grid lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <div className="relative">
          <img src="https://images.pexels.com/photos/13722878/pexels-photo-13722878.jpeg" alt="Craftsmanship" className="w-full aspect-[4/5] object-cover border border-white/10" />
          <div className="absolute -bottom-6 -right-6 bg-gold text-black p-6 hidden md:block">
            <p className="font-serif text-4xl leading-none">1</p>
            <p className="text-xs uppercase tracking-widest mt-1">Year Warranty</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <Overline>Who We Are</Overline>
        <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl leading-tight">
          A Virginia remodeling company built on <em className="gold-gradient-text">trust</em> and integrity.
        </h2>
        <p className="mt-6 text-white/60 font-body leading-relaxed">
          At GI Remodeling LLC, we believe your home should reflect the quality of life you’ve worked hard to build. From kitchens and bathrooms to full home transformations, our team of licensed craftsmen delivers meticulous work, transparent communication and results you can feel.
        </p>
        <p className="mt-4 text-white/60 font-body leading-relaxed">
          Every project is protected by our written <span className="text-gold">1-year workmanship warranty</span> — because we stand behind everything we build.
        </p>
        <Link data-testid="home-learn-more" to="/about" className="mt-8 inline-flex items-center gap-2 text-gold text-sm uppercase tracking-widest hover:text-white transition-colors">
          Learn more about us <ArrowUpRight size={16} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Stats = () => (
  <section className="border-y border-white/10 bg-[#080808]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
      {STATS.map((s, i) => (
        <Reveal key={i} delay={i * 0.08}>
          <div className="py-12 px-6 text-center">
            <p className="font-serif text-5xl gold-gradient-text">{s.n}</p>
            <p className="mt-3 text-xs uppercase tracking-widest text-white/50 font-body">{s.l}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Pillars = () => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36">
    <Reveal>
      <Overline>Why Homeowners Trust Us</Overline>
      <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl max-w-3xl leading-tight">Quality you can see. Value you can feel.</h2>
    </Reveal>
    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
      {PILLARS.map((p, i) => {
        const Icon = p.icon;
        return (
          <Reveal key={i} delay={i * 0.08}>
            <div className="bg-[#0a0a0a] p-8 md:p-10 h-full hover:bg-[#0f0f0f] transition-colors duration-500">
              <Icon size={30} className="text-gold" strokeWidth={1.4} />
              <h3 className="mt-6 font-serif text-2xl">{p.t}</h3>
              <p className="mt-3 text-white/55 font-body text-sm leading-relaxed">{p.d}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const ServicesTeaser = ({ services }) => (
  <section className="bg-[#080808] border-y border-white/10 py-28 md:py-36">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <Overline>What We Do</Overline>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl">Our Services</h2>
          </div>
          <Link data-testid="home-all-services" to="/services" className="inline-flex items-center gap-2 text-gold text-sm uppercase tracking-widest hover:text-white transition-colors">
            View all services <ArrowUpRight size={16} />
          </Link>
        </div>
      </Reveal>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {services.slice(0, 4).map((s, i) => (
          <Reveal key={s.id} delay={(i % 4) * 0.08}>
            <Link to={`/services/${s.slug}`} className="group relative block overflow-hidden aspect-[3/4] border border-white/10 hover:border-gold/40 transition-colors">
              {s.image ? (
                <img src={s.image} alt={s.title} className="h-full w-full object-cover opacity-50 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
              ) : (
                <div className="h-full w-full bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.15),transparent_60%),linear-gradient(to_bottom_right,#0d0d0d,#050505_60%,#1a1503)]">
                  <span className="absolute right-4 top-8 font-serif text-[10rem] leading-none opacity-[0.06] select-none">{s.title.charAt(0)}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 p-6"><h3 className="font-serif text-2xl">{s.title}</h3></div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WorkTeaser = ({ projects, settings }) => {
  const feature = projects && projects[0];
  const before = feature?.before_image || "https://images.pexels.com/photos/6587852/pexels-photo-6587852.jpeg";
  const after = feature?.after_image || "https://images.pexels.com/photos/8142047/pexels-photo-8142047.jpeg";
  const overline = settings?.ba_overline || "Before & After";
  const title = settings?.ba_title || "See the transformation";
  const subtitle = settings?.ba_subtitle || "Our latest 10 completed projects across Virginia — drag to reveal each one.";
  const ctaLabel = settings?.ba_cta_label || "View Before & After";
  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <Overline>{overline}</Overline>
            <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl">{title}</h2>
            {subtitle && <p className="mt-4 text-white/50 max-w-xl">{subtitle}</p>}
          </div>
          <p className="text-white/50 font-body text-sm uppercase tracking-widest hidden md:block">Drag to reveal</p>
        </div>
      </Reveal>
      <Reveal delay={0.1} className="mt-12">
        <BeforeAfterSlider before={before} after={after} beforeLabel="BEFORE" afterLabel="AFTER" />
        {feature && (
          <div className="mt-6 flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-gold">{feature.category}</p>
              <p className="font-serif text-2xl mt-1">{feature.title}</p>
              {feature.location && <p className="text-white/40 text-sm">{feature.location}</p>}
            </div>
          </div>
        )}
        <div className="mt-10 text-center">
          <Link
            data-testid="home-before-after-btn"
            to="/before-after"
            className="inline-flex items-center gap-3 bg-gold text-black text-sm font-semibold uppercase tracking-widest px-8 py-4 hover:bg-white transition-colors duration-300"
          >
            {ctaLabel} <ArrowUpRight size={18} />
          </Link>
        </div>
      </Reveal>
    </section>
  );
};

const TestimonialsTeaser = ({ items }) => {
  if (!items.length) return null;
  const shown = items.slice(0, 10);
  return (
    <section className="bg-[#080808] border-y border-white/10 py-28 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <Overline>Testimonials</Overline>
          <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl">Latest 10 Reviews</h2>
          <p className="mt-4 text-white/50 max-w-2xl">Real feedback from Virginia homeowners we've served — kitchens, bathrooms, decks, roofing, and full-home renovations.</p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {shown.map((tm, i) => (
            <Reveal key={tm.id} delay={(i % 3) * 0.08}>
              <div data-testid={`testimonial-${i}`} className="border border-white/10 p-8 h-full flex flex-col bg-[#0a0a0a] hover:border-gold/40 transition-colors">
                <div className="flex gap-1 text-gold">{Array.from({ length: tm.rating }).map((_, k) => <Star key={k} size={16} fill="#d4af37" />)}</div>
                <p className="mt-6 font-serif italic text-lg text-white/90 leading-relaxed flex-1">“{tm.quote}”</p>
                <div className="mt-8 pt-6 border-t border-white/10">
                  <p className="font-body text-white">{tm.name}</p>
                  <p className="font-body text-white/40 text-sm">{tm.location}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTA = () => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-36 text-center">
    <Reveal>
      <Overline className="justify-center">Ready to start?</Overline>
      <h2 className="mt-6 font-serif font-light text-4xl md:text-6xl leading-tight">Let’s transform your home in Virginia.</h2>
      <Link data-testid="home-cta-bottom" to="/contact" className="mt-10 inline-flex items-center gap-3 bg-gold text-black text-sm font-semibold uppercase tracking-widest px-8 py-4 hover:bg-white transition-colors duration-300">
        Request a Free Quote <ArrowUpRight size={18} />
      </Link>
    </Reveal>
  </section>
);

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState({});
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/site-settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/projects", { params: { limit: 10 } }).then((r) => setProjects(r.data)).catch(() => {});
  }, []);
  return (
    <div>
      <Hero heroImage={settings.hero_image} />
      <WhoWeAre />
      <Stats />
      <Pillars />
      <ServicesTeaser services={services} />
      <WorkTeaser projects={projects} settings={settings} />
      <TestimonialsTeaser items={testimonials} />
      <CTA />
    </div>
  );
}
